import { Link } from '@tanstack/react-router';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button, buttonVariants, Container } from '@/components/ui';

/**
 * Екран непередбаченої помилки.
 *
 * Без нього збій рендеру дає порожню білу сторінку: користувач не розуміє,
 * що сталось, і не має куди піти. Тут завжди є вихід — повторити або
 * повернутись до каталогу.
 *
 * Технічні подробиці показуємо лише в розробці: у продакшні стек нічого не дає
 * користувачу, зате може розкрити внутрішню структуру застосунку.
 */
export function ErrorScreen({ error, reset }: { error: unknown; reset?: () => void }): ReactNode {
	const message = error instanceof Error ? error.message : String(error);

	return (
		<Container className="grid place-items-center py-24 text-center sm:py-32">
			<span className="grid size-12 place-items-center rounded-full bg-danger/12 text-danger">
				<TriangleAlert className="size-6" />
			</span>

			<h1 className="mt-5 text-xl font-semibold">Something went wrong</h1>
			<p className="mt-2 max-w-md text-sm leading-relaxed text-fg-muted">
				The page failed to load. Nothing you did caused this, and nothing was lost — trying again
				usually helps.
			</p>

			<div className="mt-6 flex flex-wrap justify-center gap-3">
				{reset && (
					<Button onClick={reset}>
						<RotateCcw className="size-4" />
						Try again
					</Button>
				)}
				<Link to="/auctions" className={buttonVariants({ variant: 'secondary' })}>
					Back to catalogue
				</Link>
			</div>

			{import.meta.env.DEV && (
				<details className="mt-8 max-w-xl text-left">
					<summary className="cursor-pointer text-xs text-fg-subtle">
						Technical details (development only)
					</summary>
					<pre className="mt-2 overflow-x-auto rounded-lg border border-border bg-surface-muted p-3 text-left text-xs leading-relaxed text-fg-muted">
						{message}
					</pre>
				</details>
			)}
		</Container>
	);
}

/** Сторінка, якої немає. Пропонує куди піти, а не просто повідомляє про 404. */
export function NotFoundScreen(): ReactNode {
	return (
		<Container className="grid place-items-center py-24 text-center sm:py-32">
			<p className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">404</p>
			<h1 className="mt-3 text-2xl font-semibold">This page does not exist</h1>
			<p className="mt-2 max-w-md text-sm leading-relaxed text-fg-muted">
				The link may be out of date, or the lot it pointed to has already been sold.
			</p>
			<div className="mt-6 flex flex-wrap justify-center gap-3">
				<Link to="/auctions" className={buttonVariants()}>
					Browse auctions
				</Link>
				<Link to="/" className={buttonVariants({ variant: 'secondary' })}>
					Go home
				</Link>
			</div>
		</Container>
	);
}
